import { Skeleton } from 'components/common';
import { calculateNutritionalValues } from 'lib/nutrition';
import { Variant } from 'lib/product';
import {
  getDefaultAddonOptionValue,
  getNextVisibleSelectedAddons,
  getSelectedAddonOptionValueIds,
  useProductVariantAddonOptions,
  useProductVariantAddonRules,
} from 'lib/productAddon';
import { memo, useMemo } from 'react';
import ProductNutrient from '../ProductNutrient';

interface CustomizableProductNutritionProps {
  productVariant: Variant;
  initialSelectedAddons?: Record<number, number>;
}

const CustomizableProductNutrition = memo(
  ({
    productVariant,
    initialSelectedAddons,
  }: CustomizableProductNutritionProps) => {
    const {
      addonOptions,
      isLoading: isAddonOptionsLoading,
      selectAddonOptionValueById,
      selectIngredientById,
    } = useProductVariantAddonOptions(productVariant.id);
    const {
      addonRules,
      isLoading: isAddonRulesLoading,
    } = useProductVariantAddonRules(productVariant.id);

    const defaultSelectedAddons = useMemo(() => {
      if (isAddonOptionsLoading) return {};
      if (initialSelectedAddons) return initialSelectedAddons;

      return addonOptions?.reduce((result, addonOption) => {
        result[addonOption.id] = getDefaultAddonOptionValue(
          addonOption,
        )?.id;

        return result;
      }, {});
    }, [addonOptions, initialSelectedAddons, isAddonOptionsLoading]);

    const nextVisibleSelectedAddons = useMemo(() => {
      // @ts-ignore
      return getNextVisibleSelectedAddons({
        addonOptions: addonOptions ?? [],
        addonRules: addonRules ?? [],
        selectedAddons: defaultSelectedAddons,
      });
    }, [addonOptions, addonRules, defaultSelectedAddons]);

    const selectedAddonOptionValueIds = useMemo(() => {
      return getSelectedAddonOptionValueIds(
        nextVisibleSelectedAddons,
      );
    }, [nextVisibleSelectedAddons]);

    const selectedAddonOptionValues = selectedAddonOptionValueIds.map(
      (selectedAddonOptionValueId) =>
        selectAddonOptionValueById(selectedAddonOptionValueId),
    );

    const nutritionalValue = useMemo(
      () =>
        calculateNutritionalValues(
          productVariant,
          selectedAddonOptionValues,
          selectIngredientById,
        ),
      [
        productVariant,
        selectIngredientById,
        selectedAddonOptionValues,
      ],
    );

    const { calorie, fat, carb, protein } = nutritionalValue;

    return (
      <div className="tw-flex tw-gap-2 md:tw-gap-2.5 xl:tw-gap-8 tw-flex-1 tw-items-end">
        {isAddonOptionsLoading || isAddonRulesLoading ? (
          <Skeleton />
        ) : (
          <>
            <ProductNutrient name="kCal" value={calorie ?? 0} />
            <ProductNutrient name="Fats" value={fat ?? 0} unit="g" />
            <ProductNutrient
              name="Carbs"
              value={carb ?? 0}
              unit="g"
            />
            <ProductNutrient
              name="Protein"
              value={protein ?? 0}
              unit="g"
            />
          </>
        )}
      </div>
    );
  },
);

export default CustomizableProductNutrition;
